import { NavLink } from 'react-router-dom';

import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import type { NavigationItem } from '@/config/navigation';
import { cn } from '@/utils/helpers/cn';

import type { ReactNode } from 'react';

/*
 * As peças de uma célula da `BottomNav`.
 *
 * Moram fora de `BottomNav.tsx` porque a barra tem **dois** tipos de célula: o
 * link de aba (`BottomNavLink`) e o botão que abre o menu (`AppMenuTab`, em
 * `AppMenu.tsx`), que é a última célula desde que menu e perfil trocaram de
 * lugar (planilha, 2026-10). Os dois precisam da mesma caixa e do mesmo
 * rótulo — as trilhas do grid são calibradas, e uma célula 2px diferente tira
 * o SOS do centro. Uma régua só, `bottomNavItemClassName` + `BottomNavLabel`,
 * em vez de duas cópias que divergem no primeiro ajuste.
 */

/**
 * Botão de socorro — a ação principal do app.
 *
 * Círculo vermelho de 44px que se projeta para fora do topo da barra, com 17px
 * dos 44 sobre o mapa. A referência visual (`docs/referencias-visuais/`, medida
 * em 2026-07-20) tem ~21px acima da barra num quadro de 390px; ficamos um pouco
 * abaixo disso, e a diferença nunca foi contestada no desenho.
 *
 * ## Como a geometria funciona
 *
 * O problema é que o círculo precisa subir **sem levar o rótulo junto** — senão
 * "Socorro" sai da linha de base dos vizinhos.
 *
 * A solução é um invólucro de **altura fixa igual à de um ícone normal** (24px)
 * com o círculo transbordando dele. Como a altura do invólucro é fixa, o quanto
 * o círculo sobe não afeta o fluxo: o rótulo se posiciona como se ali houvesse
 * um ícone comum. Toda a geometria do destaque cabe num número só, o `-mt-*` do
 * círculo — não há `absolute`, `top` nem `translate` para conciliar.
 *
 * ⚠️ **A cor diverge da referência de propósito.** O vermelho de lá é `#F13430`,
 * que dá **3,98:1** com branco e reprova em WCAG AA — tanto para o "SOS" dentro
 * do círculo quanto para o rótulo sobre fundo claro. Usamos `--destructive`
 * (`#CE0000`, 5,79:1). Num botão de emergência acionado na rua e sob sol, este é
 * o último lugar do app onde vale ceder contraste. Ver docs/apendice-tema-arcus.md.
 */
function EmergencyMark() {
  return (
    // Altura de um ícone normal (`Icon size="lg"` = 24px). É ela que mantém o
    // rótulo alinhado com os vizinhos, seja qual for o tamanho do círculo.
    <span aria-hidden className="block h-6 w-full relative">
      <span
        className={cn(
          'bg-destructive text-destructive-foreground grid size-11 place-items-center rounded-full',
          // Separa o círculo do mapa na parte que passa da barra. Sem o anel, a
          // borda vermelha encosta em tile claro e some.
          // 'ring-surface shadow-lg ring-16',
          // O único número da geometria: 30px acima do topo do invólucro deixa
          // 17px do círculo sobre o mapa. Aumentar sobe o círculo e não mexe em
          // mais nada — nem na altura da barra, nem na linha dos rótulos.
          'mx-auto -mt-7.5',
        )}
      >
        <Text variant="caption" as="span" className="text-sm leading-none font-bold">
          SOS
        </Text>
      </span>
      {/*
        Centralizado por `left-1/2` + `-translate-x-1/2`, no mesmo eixo do
        `mx-auto` do círculo vermelho: um `left` fixo só acerta numa largura de
        célula específica e desloca o recorte em todas as outras.
      */}
      <span className="bg-surface border-t-destructive absolute -top-12 left-1/2 -z-10 block min-h-26 min-w-20 -translate-x-1/2 rounded-t-full border-t-2" />
    </span>
  );
}

/**
 * Contador de não lidas sobre o ícone da aba.
 *
 * ⚠️ **Nada aqui pode ocupar espaço no fluxo.** As trilhas do grid da barra são
 * calibradas (ver o comentário do `<ul>`), e qualquer largura extra numa célula
 * empurra as vizinhas e tira o SOS do centro. Por isso o badge é `absolute`
 * dentro de um invólucro do tamanho exato do ícone: ele transborda sobre o
 * vizinho — o que o `overflow-visible` da barra já permite — em vez de alargar
 * a célula.
 *
 * O número é `aria-hidden` e o nome acessível vem do `sr-only` que o `BottomNavLink`
 * põe **depois** do rótulo: lido cru, "12 Avisos" não diz o que são os doze, e a
 * ordem importa — o leitor anuncia "Avisos, 12 avisos não lidos", que é a frase
 * na ordem em que se pensa. Badge é informação, não enfeite: um ponto colorido
 * sozinho não existiria para quem usa leitor de tela.
 *
 * `bg-primary` e não `bg-destructive` de propósito: o vermelho da barra é do
 * botão de socorro, e um segundo vermelho a dois dedos dele disputaria a
 * urgência que só o SOS tem.
 */
function UnreadBadge({ count }: { count: number }) {
  return (
    <span
      aria-hidden
      className={cn(
        'bg-primary text-primary-foreground absolute -top-1 left-1/2 grid min-w-4 place-items-center rounded-full px-1',
        'text-[10px] leading-4 font-semibold',
      )}
    >
      {/* Acima de 9 o número deixa de ser contagem e vira "muitos": dois
          dígitos alargariam a pastilha sobre o ícone vizinho. */}
      {count > 9 ? '9+' : count}
    </span>
  );
}

/**
 * A caixa de uma célula da barra, para link ou botão.
 *
 * `justify-end` + `pb-1.5`: os rótulos alinham pela base, então o círculo de
 * 44px cresce para cima sem desalinhar o texto dos vizinhos. `w-full` +
 * `min-w-0`: o alvo ocupa a célula inteira e a célula pode encolher. Sem
 * `min-w-0`, o `min-width:auto` do flex faz o rótulo mais longo
 * ("Notificações") alargar a própria célula e empurrar as outras — o efeito
 * medido foi o SOS 55px fora do centro da tela.
 *
 * Nenhum `-mt`/`pt` de compensação aqui, de propósito: o círculo do SOS é
 * descendente do `<a>`, então o toque nele borbulha para o link mesmo
 * transbordando a caixa. Esticar a caixa para cima só criaria faixa invisível
 * sobre o mapa — e ela dispararia o fluxo de emergência num arrasto do mapa.
 */
export function bottomNavItemClassName({
  isActive,
  isEmphasis = false,
}: {
  isActive: boolean;
  isEmphasis?: boolean;
}) {
  return cn(
    'flex min-h-14 w-full min-w-0 flex-col items-center justify-end gap-1 px-1 pb-1.5',
    'active:bg-muted/60',
    isEmphasis ? 'text-destructive' : undefined,
    !isEmphasis && (isActive ? 'text-primary' : 'text-muted-foreground'),
  );
}

/**
 * O rótulo da célula. É sempre visível: ícone sozinho numa barra de emergência
 * obriga a decifrar pictograma, que é exatamente o que ninguém faz sob estresse.
 *
 * `block` é obrigatório junto de `truncate`: `Text` renderiza um `span`, e
 * `overflow`/`width` não se aplicam a elemento inline. Sem ele "Notificações"
 * alarga a própria célula e empurra as outras — o efeito medido foi só 4 dos 5
 * itens cabendo na tela.
 */
export function BottomNavLabel({
  isStrong,
  children,
}: {
  /** Aba ativa ou item de destaque: o peso sobe junto com a cor. */
  isStrong: boolean;
  children: ReactNode;
}) {
  return (
    <Text
      variant="caption"
      as="span"
      className={cn(
        'block w-full truncate text-center text-[10px] leading-none',
        isStrong && 'font-medium',
      )}
    >
      {children}
    </Text>
  );
}

export function BottomNavLink({
  item,
  replace,
  unreadCount,
  onGuestBlocked,
}: {
  item: NavigationItem;
  /** Substitui a entrada de histórico em vez de empilhar. Decidido por quem monta a barra. */
  replace?: boolean;
  /** Não lidas a anunciar nesta aba. Zero (ou ausente) não desenha badge. */
  unreadCount?: number;
  /** Chamado no lugar da navegação quando um convidado toca uma ação de conta. */
  onGuestBlocked?: (to: string) => void;
}) {
  const isEmphasis = Boolean(item.emphasis);
  const hasBadge = !isEmphasis && (unreadCount ?? 0) > 0;

  return (
    <NavLink
      to={item.to}
      replace={replace}
      // `end` na Home: sem ele, `/` casa com **toda** rota e a aba Mapa ficaria
      // permanentemente ativa junto da aba real.
      end={item.to === '/'}
      onClick={(event) => {
        if (onGuestBlocked) {
          event.preventDefault();
          onGuestBlocked(item.to);
        }
      }}
      className={({ isActive }) => bottomNavItemClassName({ isActive, isEmphasis })}
    >
      {({ isActive }) => (
        <>
          {isEmphasis ? (
            <EmergencyMark />
          ) : (
            // Invólucro do tamanho do ícone (24px) só para ancorar o badge: sem
            // um `relative` aqui, o `absolute` dele subiria até a barra e o
            // número apareceria no canto da tela.
            <span className="relative block size-6">
              <Icon as={item.icon} size="lg" />
              {hasBadge ? <UnreadBadge count={unreadCount ?? 0} /> : null}
            </span>
          )}
          <BottomNavLabel isStrong={isActive || isEmphasis}>{item.label}</BottomNavLabel>

          {/* Depois do rótulo, e fora do `Text`, para o leitor de tela anunciar
              "Avisos, 3 avisos não lidos" — na ordem em que a frase se pensa.
              `sr-only` não ocupa espaço no fluxo, então a célula do grid
              continua com a mesma largura. */}
          {hasBadge ? (
            <span className="sr-only">
              {unreadCount} {unreadCount === 1 ? 'aviso não lido' : 'avisos não lidos'}
            </span>
          ) : null}
        </>
      )}
    </NavLink>
  );
}
